import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import LockOutlineRoundedIcon from "@mui/icons-material/LockOutlineRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import { useChangePasswordAuthenticated } from "../mutations";
import {
  getPasswordRequirements,
  isPasswordValid,
} from "../utils/passwordValidation";

interface AuthenticatedChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AuthenticatedChangePasswordDialog: React.FC<
  AuthenticatedChangePasswordDialogProps
> = ({ open, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const changePasswordMutation = useChangePasswordAuthenticated();

  const requirements = useMemo(() => {
    return getPasswordRequirements(newPassword, confirmPassword);
  }, [newPassword, confirmPassword]);

  const hasMatchingPasswords =
    requirements.find((req) => req.key === "passwordsMatch")?.isValid ?? false;
  const hasValidNewPassword = isPasswordValid(requirements);
  const sameAsCurrent = newPassword.length > 0 && currentPassword === newPassword;

  const showCurrentPasswordError = touched && currentPassword.length === 0;
  const showNewPasswordError =
    touched && (!hasValidNewPassword || sameAsCurrent);
  const showConfirmPasswordError = touched && !hasMatchingPasswords;

  useEffect(() => {
    if (!open) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setTouched(false);
      setError("");
      setSuccess("");
    }
  }, [open]);

  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    hasValidNewPassword &&
    !sameAsCurrent &&
    !changePasswordMutation.isPending;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    setError("");
    setSuccess("");

    if (!canSubmit) {
      return;
    }

    try {
      const response = await changePasswordMutation.mutateAsync({
        previousPassword: currentPassword,
        proposedPassword: newPassword,
      });
      setSuccess(response.message || "Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTouched(false);
    } catch (err: unknown) {
      const responseData = (
        err as {
          response?: {
            data?: { message?: string; error?: string; details?: string };
          };
        }
      )?.response?.data;

      const message =
        responseData?.details ||
        responseData?.error ||
        responseData?.message ||
        (err instanceof Error ? err.message : "Failed to change password.");

      setError(message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={changePasswordMutation.isPending ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? <Alert severity="success">{success}</Alert> : null}

            <TextField
              fullWidth
              label="Current password"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showCurrentPasswordError}
              helperText={
                showCurrentPasswordError ? "Current password is required." : ""
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle current password visibility"
                      onClick={() => setShowCurrentPassword((prev) => !prev)}
                    >
                      {showCurrentPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showNewPasswordError}
              helperText={
                touched && sameAsCurrent
                  ? "New password must be different from current password."
                  : showNewPasswordError
                    ? "New password must meet all requirements."
                    : ""
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle new password visibility"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                    >
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm new password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showConfirmPasswordError}
              helperText={showConfirmPasswordError ? "Passwords do not match." : ""}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {newPassword ? (
              <PasswordStrengthIndicator requirements={requirements} showProgress />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            color="inherit"
            disabled={changePasswordMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
          >
            {changePasswordMutation.isPending ? "Saving..." : "Update password"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
